import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { upsertOrganizationLocal, upsertProfileLocal } from '../db/sync/membersSync';
import { buildDisplayName, ensureSelfOnOrgRoster } from '../db/sync/rosterSync';
import { writeCachedOrgContext, useMeetingLiveStore } from '../store/meetingLiveStore';
import { writeActiveOrganizationId } from '../auth/moduleAccess';

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshMemberships } = useAuth();
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!hasSupabaseConfig()) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-pp-paper px-4 text-pp-ink">
        <p className="max-w-md text-center text-sm text-pp-muted">
          Add <code className="rounded bg-pp-paper px-1">REACT_APP_SUPABASE_URL</code> and{' '}
          <code className="rounded bg-pp-paper px-1">REACT_APP_SUPABASE_ANON_KEY</code> to{' '}
          <code className="rounded bg-pp-paper px-1">.env</code> to enable sign-in, or run without them to use the app
          locally without cloud sync.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 rounded-xl bg-pp-ink px-4 py-2 text-sm font-medium text-white"
        >
          Continue without Supabase
        </button>
      </div>
    );
  }

  const supabase = getSupabaseClient();

  const onLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      const list = await refreshMemberships();
      if (!list?.length) {
        navigate('/orgs');
      } else if (list.length === 1) {
        navigate(`/orgs/${list[0].organization_id}`);
      } else {
        navigate('/orgs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    setError(null);
    if (!organizationName.trim()) {
      setError('Organization name is required');
      return;
    }
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      setError('First and last name are required');
      return;
    }
    const display = buildDisplayName(first, last);
    setBusy(true);
    try {
      const { data: signData, error: signErr } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: first,
            last_name: last,
            display_name: display,
          },
        },
      });
      if (signErr) throw signErr;
      const userId = signData.user?.id;
      if (!userId) {
        setError('Check your email to confirm your account, then sign in.');
        setBusy(false);
        return;
      }
      // With “Confirm email” enabled, Supabase returns a user but no session.
      // Org creation RPCs require an authenticated session.
      if (!signData.session) {
        setError(
          'Account created. Confirm your email (check inbox), then sign in. If you have no organization yet, create one after signing in — or turn off “Confirm email” in Supabase Auth settings for smoother signup.'
        );
        setMode('login');
        setBusy(false);
        return;
      }

      // Ensure profile shell
      await supabase.from('profiles').upsert({
        id: userId,
        first_name: first,
        last_name: last,
        display_name: display,
      });

      const { data: orgId, error: rpcErr } = await supabase.rpc('signup_create_org_and_profile', {
        org_name: organizationName.trim(),
        officer_role: 'Admin',
      });
      if (rpcErr) throw rpcErr;

      const { data: orgRow, error: orgFetchErr } = await supabase
        .from('organizations')
        .select('id, name, quorum_threshold')
        .eq('id', orgId)
        .single();
      if (orgFetchErr || !orgRow) throw orgFetchErr ?? new Error('Could not load new organization');

      await upsertOrganizationLocal({
        id: orgRow.id,
        name: orgRow.name,
        quorum_threshold: orgRow.quorum_threshold ?? 50,
      });
      await upsertProfileLocal({ id: userId, display_name: display });
      await ensureSelfOnOrgRoster(orgRow.id, first, last);

      useMeetingLiveStore.getState().setOrganizationContext(orgRow.id, orgRow.name);
      writeCachedOrgContext({
        organizationId: orgRow.id,
        organizationName: orgRow.name,
        userId,
      });
      writeActiveOrganizationId(orgRow.id);

      await refreshMemberships();
      navigate(`/orgs/${orgRow.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-pp-paper px-4 py-12 text-pp-ink">
      <div className="w-full max-w-md rounded-2xl border border-pp-line/80 bg-pp-surface p-8 shadow-pp">
        <Link to="/" className="text-xs font-medium text-pp-muted hover:text-pp-ink">
          ← Back to home
        </Link>
        <h1 className="mt-4 text-xl font-semibold tracking-tight">Pocket Parliamentarian</h1>
        <p className="mt-1 text-sm text-pp-muted">Sign in or create an organization</p>

        <div className="mt-6 flex gap-2 rounded-full bg-pp-paper p-1">
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium ${
              mode === 'login' ? 'bg-pp-ink text-white' : 'text-pp-muted'
            }`}
          >
            Log in
          </button>
          <button
            type="button"
            onClick={() => setMode('signup')}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium ${
              mode === 'signup' ? 'bg-pp-ink text-white' : 'text-pp-muted'
            }`}
          >
            Create org
          </button>
        </div>

        {mode === 'login' ? (
          <form className="mt-6 space-y-4" onSubmit={onLogin}>
            <div>
              <label className="text-xs font-medium text-pp-muted">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <p className="text-center text-xs text-pp-muted">
              Joining an existing organization? Use the invite link from your admin.
            </p>
          </form>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={onSignup}>
            <div>
              <label className="text-xs font-medium text-pp-muted">Organization name</label>
              <input
                type="text"
                required
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                placeholder="e.g. Northside Student Senate"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-pp-muted">First name</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  placeholder="Jordan"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-pp-muted">Last name</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  placeholder="Shaw"
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <p className="text-xs text-pp-muted">
              You will be the organization Admin and can invite officers afterward.
            </p>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Creating account…' : 'Create organization & account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
